/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { PaginatedReceiptList } from '../models/PaginatedReceiptList';
import type { PatchedPostReceiptRequest } from '../models/PatchedPostReceiptRequest';
import type { PostReceiptRequest } from '../models/PostReceiptRequest';
import type { Receipt } from '../models/Receipt';
import type { ReceiptInfo } from '../models/ReceiptInfo';
import type { ReceiptRejectRequest } from '../models/ReceiptRejectRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ReceiptsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * API endpoint to view and edit employee receipts
     * @returns PaginatedReceiptList
     * @throws ApiError
     */
    public receiptsList({
        amount,
        benefit,
        company,
        dateAfter,
        dateBefore,
        employee,
        employeeName,
        limit,
        note,
        offset,
        ordering,
        search,
        status,
    }: {
        amount?: number,
        /**
         * * `betreuungskosten` - betreuungskosten
         * * `öpnv_tickets` - öpnv_tickets
         * * `telefonkosten` - telefonkosten
         * * `essenszuschuss` - essenszuschuss
         * * `internetpauschale` - internetpauschale
         */
        benefit?: Array<'betreuungskosten' | 'essenszuschuss' | 'internetpauschale' | 'telefonkosten' | 'öpnv_tickets'>,
        company?: string,
        dateAfter?: string,
        dateBefore?: string,
        employee?: string,
        employeeName?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        note?: string,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * A search term.
         */
        search?: string,
        /**
         * * `DRAFTED` - Drafted
         * * `SUBMITTED` - Submitted
         * * `REJECTED` - Rejected
         * * `APPROVED` - Approved
         * * `BILLED` - Billed
         * * `ARCHIVED` - Archived
         * * `DELETION_SUBMITTED` - Submitted for deletion
         */
        status?: 'APPROVED' | 'ARCHIVED' | 'BILLED' | 'DELETION_SUBMITTED' | 'DRAFTED' | 'REJECTED' | 'SUBMITTED',
    }): CancelablePromise<PaginatedReceiptList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/receipts/',
            query: {
                'amount': amount,
                'benefit': benefit,
                'company': company,
                'date_after': dateAfter,
                'date_before': dateBefore,
                'employee': employee,
                'employee_name': employeeName,
                'limit': limit,
                'note': note,
                'offset': offset,
                'ordering': ordering,
                'search': search,
                'status': status,
            },
        });
    }

    /**
     * API endpoint to view and edit employee receipts
     * @returns Receipt
     * @throws ApiError
     */
    public receiptsCreate({
        requestBody,
    }: {
        requestBody: PostReceiptRequest,
    }): CancelablePromise<Receipt> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/receipts/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employee receipts
     * @returns Receipt
     * @throws ApiError
     */
    public receiptsRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this receipt.
         */
        id: string,
    }): CancelablePromise<Receipt> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/receipts/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint to view and edit employee receipts
     * @returns Receipt
     * @throws ApiError
     */
    public receiptsUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this receipt.
         */
        id: string,
        requestBody: PostReceiptRequest,
    }): CancelablePromise<Receipt> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/receipts/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employee receipts
     * @returns Receipt
     * @throws ApiError
     */
    public receiptsPartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this receipt.
         */
        id: string,
        requestBody?: PatchedPostReceiptRequest,
    }): CancelablePromise<Receipt> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/receipts/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employee receipts
     * @returns void
     * @throws ApiError
     */
    public receiptsDestroy({
        id,
    }: {
        /**
         * A UUID string identifying this receipt.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/receipts/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint to view and edit employee receipts
     * @returns Receipt
     * @throws ApiError
     */
    public receiptsApproveUpdate({
        id,
    }: {
        /**
         * A UUID string identifying this receipt.
         */
        id: string,
    }): CancelablePromise<Receipt> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/receipts/{id}/approve/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint to view and edit employee receipts
     * @returns Receipt
     * @throws ApiError
     */
    public receiptsRejectUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this receipt.
         */
        id: string,
        requestBody?: ReceiptRejectRequest,
    }): CancelablePromise<Receipt> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/receipts/{id}/reject/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employee receipts
     * @returns Receipt
     * @throws ApiError
     */
    public receiptsSettleUpdate({
        id,
    }: {
        /**
         * A UUID string identifying this receipt.
         */
        id: string,
    }): CancelablePromise<Receipt> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/receipts/{id}/settle/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint to view and edit employee receipts
     * @returns Receipt
     * @throws ApiError
     */
    public receiptsConfirmReceiptForMonthUpdate({
        date,
        receipt,
    }: {
        date: string,
        receipt: string,
    }): CancelablePromise<Receipt> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/receipts/confirm_receipt_for_month/',
            query: {
                'date': date,
                'receipt': receipt,
            },
        });
    }

    /**
     * API endpoint to view and edit employee receipts
     * @returns ReceiptInfo
     * @throws ApiError
     */
    public receiptsInfoList({
        amount,
        benefit,
        company,
        dateAfter,
        dateBefore,
        employee,
        employeeName,
        note,
        ordering,
        search,
        status,
    }: {
        amount?: number,
        /**
         * * `betreuungskosten` - betreuungskosten
         * * `öpnv_tickets` - öpnv_tickets
         * * `telefonkosten` - telefonkosten
         * * `essenszuschuss` - essenszuschuss
         * * `internetpauschale` - internetpauschale
         */
        benefit?: Array<'betreuungskosten' | 'essenszuschuss' | 'internetpauschale' | 'telefonkosten' | 'öpnv_tickets'>,
        company?: string,
        dateAfter?: string,
        dateBefore?: string,
        employee?: string,
        employeeName?: string,
        note?: string,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * A search term.
         */
        search?: string,
        /**
         * * `DRAFTED` - Drafted
         * * `SUBMITTED` - Submitted
         * * `REJECTED` - Rejected
         * * `APPROVED` - Approved
         * * `BILLED` - Billed
         * * `ARCHIVED` - Archived
         * * `DELETION_SUBMITTED` - Submitted for deletion
         */
        status?: 'APPROVED' | 'ARCHIVED' | 'BILLED' | 'DELETION_SUBMITTED' | 'DRAFTED' | 'REJECTED' | 'SUBMITTED',
    }): CancelablePromise<Array<ReceiptInfo>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/receipts/info/',
            query: {
                'amount': amount,
                'benefit': benefit,
                'company': company,
                'date_after': dateAfter,
                'date_before': dateBefore,
                'employee': employee,
                'employee_name': employeeName,
                'note': note,
                'ordering': ordering,
                'search': search,
                'status': status,
            },
        });
    }

}
